import type { ElementType } from 'react';
import { cn } from '@/lib/utils';

export interface ServiceCardItem {
  id: string;
  name: string;
  price?: number | null;
  description?: string | null;
  /** Tipo appuntamento a cui il servizio è già abbinato (warning UX). */
  linkedToOtherTypeName?: string | null;
}

interface BaseProps {
  services: ServiceCardItem[];
  emptyText?: string;
  showPrice?: boolean;
  className?: string;
}

interface MultiProps extends BaseProps {
  mode?: 'multi';
  selectedIds: string[];
  onToggle: (id: string) => void;
}

interface ReadonlyProps extends BaseProps {
  mode: 'readonly';
  selectedIds?: never;
  onToggle?: never;
}

type Props = MultiProps | ReadonlyProps;

export default function ServiceCardList(props: Props) {
  const { services, emptyText, showPrice = true, className, mode = 'multi' } = props;

  if (services.length === 0) {
    return (
      <p className="text-[11px] text-gray-500 dark:text-gray-400 italic">
        {emptyText || 'Nessun servizio disponibile.'}
      </p>
    );
  }

  return (
    <div
      className={cn(
        'grid gap-2 grid-cols-1 sm:grid-cols-2',
        className
      )}
    >
      {services.map((svc) => {
        const selected =
          mode === 'multi' ? (props as MultiProps).selectedIds.includes(svc.id) : false;
        const interactive = mode === 'multi';

        const Tag: ElementType = interactive ? 'button' : 'div';
        const interactiveProps = interactive
          ? {
              type: 'button' as const,
              onClick: () => (props as MultiProps).onToggle(svc.id),
              'aria-pressed': selected,
            }
          : {};

        return (
          <Tag
            key={svc.id}
            {...interactiveProps}
            className={cn(
              'relative text-left p-2.5 rounded-lg border transition-colors min-w-0',
              interactive && 'cursor-pointer hover:border-purple-400 dark:hover:border-purple-500',
              selected
                ? 'border-purple-500 bg-purple-100/70 dark:bg-purple-900/40 ring-1 ring-purple-400'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
            )}
          >
            {interactive && (
              <span
                className={cn(
                  'absolute top-1.5 right-1.5 inline-flex items-center justify-center w-4 h-4 rounded border text-[10px] leading-none',
                  selected
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-transparent'
                )}
                aria-hidden
              >
                ✓
              </span>
            )}

            <div className="flex items-start gap-2 pr-5 min-w-0">
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {svc.name}
                </span>
                {svc.description && (
                  <span className="block text-[10px] text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">
                    {svc.description}
                  </span>
                )}
              </span>
              {showPrice && typeof svc.price === 'number' && svc.price > 0 && (
                <span className="text-[11px] font-semibold text-purple-700 dark:text-purple-300 whitespace-nowrap">
                  € {svc.price.toFixed(2)}
                </span>
              )}
            </div>

            {svc.linkedToOtherTypeName && (
              <span className="block mt-1 text-[10px] text-amber-600 dark:text-amber-400 truncate">
                → già su «{svc.linkedToOtherTypeName}»
              </span>
            )}
          </Tag>
        );
      })}
    </div>
  );
}
